/**
 * SAI SANKOH — CART DRAWER
 * File: assets/sai-cart-drawer.js
 *
 * Uses Shopify AJAX Cart API:
 *   GET  /cart.js          — fetch current cart
 *   POST /cart/add.js      — add item
 *   POST /cart/change.js   — update quantity / remove
 *
 * Dispatches custom events:
 *   sai:cart:open          — open the drawer
 *   sai:cart:updated       — cart data changed
 */

(function () {
  'use strict';

  /* ── CONFIG ── */
  var FREE_SHIPPING_THRESHOLD = 50000; // $500.00 in cents
  var RECS_COLLECTION = 'new-arrivals'; // final fallback only

  /* ── RECOMMENDATION CONTEXT (most-recently-added item) ── */
  var lastAddedContext = null;

  /* ── THEME ATC EVENT INTERCEPTION ── */
  var _nativeFetch = window.fetch;
  window.fetch = function(url, options) {
    var result = _nativeFetch.apply(this, arguments);
    if (typeof url === 'string' && url.indexOf('/cart/add') !== -1) {
      result.then(function(response) {
        if (response.ok) {
          response.clone().json().then(function(data) {
            if (data && (data.id || data.items)) {
              if (data && data.id) lastAddedContext = data;
              openDrawer();
              loadRecs(data && data.id ? data : null);
              document.dispatchEvent(new CustomEvent('sai:cart:updated'));
            }
          }).catch(function(){});
        }
      }).catch(function(){});
    }
    return result;
  };

  /* ── ELEMENTS ── */
  var drawer   = document.getElementById('sai-cart-drawer');
  var overlay  = document.getElementById('sai-cart-overlay');
  var closeBtn = document.getElementById('sai-cart-close');
  var itemsEl  = document.getElementById('sai-cart-items');
  var emptyEl  = document.getElementById('sai-cart-empty');
  var countEl  = document.getElementById('sai-cart-count');
  var subtotalEl = document.getElementById('sai-cart-subtotal');
  var freeShipEl = document.getElementById('sai-cart-free-ship');
  var shipBarEl  = document.getElementById('sai-cart-shipping-bar');
  var shipTextEl = document.getElementById('sai-cart-shipping-text');
  var shipFillEl = document.getElementById('sai-cart-shipping-fill');
  var shipRemEl  = document.getElementById('sai-cart-shipping-remaining');
  var afterpayEl = document.getElementById('sai-cart-afterpay');
  var afterpayAmt = document.getElementById('sai-cart-afterpay-amt');
  var recsGrid   = document.getElementById('sai-cart-recs-grid');
  var recsEl     = document.getElementById('sai-cart-recs');
  var ftEl       = document.getElementById('sai-cart-ft');
  var contBtn    = document.getElementById('sai-cart-continue-btn');

  /* ── UTILS ── */
  function formatMoney(cents) {
    if (cents === 0) return 'Free';
    return '$' + (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function fetchCart() {
    return fetch('/cart.js', {
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }
    }).then(function (r) { return r.json(); });
  }

  /* ── OPEN / CLOSE ── */
  function openDrawer() {
    if (!drawer) return;
    drawer.classList.add('sai-cart-open');
    drawer.setAttribute('aria-hidden', 'false');
    if (overlay) {
      overlay.classList.add('sai-cart-open');
      overlay.setAttribute('aria-hidden', 'false');
    }
    document.body.classList.add('sai-cart-active');
    refreshCart();
  }

  function closeDrawer() {
    if (!drawer) return;
    drawer.classList.remove('sai-cart-open');
    drawer.setAttribute('aria-hidden', 'true');
    if (overlay) {
      overlay.classList.remove('sai-cart-open');
      overlay.setAttribute('aria-hidden', 'true');
    }
    document.body.classList.remove('sai-cart-active');
  }

  if (closeBtn) closeBtn.addEventListener('click', closeDrawer);
  if (overlay)  overlay.addEventListener('click', closeDrawer);
  if (contBtn)  contBtn.addEventListener('click', closeDrawer);

  // Close on Escape
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closeDrawer();
  });

  /* ── RENDER CART ── */
  function renderCart(cart) {
    if (!itemsEl) return;

    var total = cart.total_price;
    var itemCount = cart.item_count;

    // Update count badge
    if (countEl) {
      countEl.textContent = itemCount;
      countEl.style.display = itemCount > 0 ? 'flex' : 'none';
    }

    // Update header cart icons across the theme
    document.querySelectorAll('[data-cart-count], .cart-count, .header-cart-count').forEach(function (el) {
      el.textContent = itemCount;
    });

    // Empty state
    if (itemCount === 0) {
      if (emptyEl)  emptyEl.style.display  = 'flex';
      if (itemsEl)  itemsEl.style.display  = 'none';
      if (ftEl)     ftEl.style.display     = 'none';
      if (recsEl)   recsEl.style.display   = 'none';
      if (shipBarEl) shipBarEl.style.display = 'none';
      return;
    }

    if (emptyEl)  emptyEl.style.display  = 'none';
    if (itemsEl)  itemsEl.style.display  = 'block';
    if (ftEl)     ftEl.style.display     = 'block';

    // Render items
    itemsEl.innerHTML = cart.items.map(function (item) {
      var imgUrl = item.image ? item.image.replace('.jpg', '_180x.jpg').replace('.png', '_180x.png').replace('.webp', '_180x.webp') : '';
      var hasVariant = item.variant_title && item.variant_title !== 'Default Title';
      return [
        '<div class="sai-cart-item" data-key="' + item.key + '" data-variant-id="' + item.variant_id + '">',
          '<a href="' + item.url + '" class="sai-cart-item-img">',
            imgUrl ? '<img src="' + imgUrl + '" alt="' + item.title.replace(/"/g, '&quot;') + '" loading="lazy">' : '',
          '</a>',
          '<div class="sai-cart-item-info">',
            '<a href="' + item.url + '" class="sai-cart-item-title">' + item.product_title + '</a>',
            hasVariant ? '<div class="sai-cart-item-variant">' + item.variant_title + '</div>' : '',
            '<div class="sai-cart-item-price">' + formatMoney(item.price) + '</div>',
            '<div class="sai-cart-item-qty">',
              '<button class="sai-cart-qty-btn sai-cart-qty-minus" data-key="' + item.key + '" data-qty="' + (item.quantity - 1) + '" aria-label="Decrease quantity">−</button>',
              '<span class="sai-cart-qty-num">' + item.quantity + '</span>',
              '<button class="sai-cart-qty-btn sai-cart-qty-plus" data-key="' + item.key + '" data-qty="' + (item.quantity + 1) + '" aria-label="Increase quantity">+</button>',
            '</div>',
          '</div>',
          '<button class="sai-cart-item-remove" data-key="' + item.key + '" aria-label="Remove ' + item.product_title.replace(/"/g, '&quot;') + '">',
            '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>',
          '</button>',
        '</div>'
      ].join('');
    }).join('');

    // Qty and remove listeners
    itemsEl.querySelectorAll('.sai-cart-qty-btn').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var key = this.dataset.key;
        var qty = parseInt(this.dataset.qty, 10);
        updateItem(key, Math.max(0, qty));
      });
    });
    itemsEl.querySelectorAll('.sai-cart-item-remove').forEach(function (btn) {
      btn.addEventListener('click', function () {
        updateItem(this.dataset.key, 0);
      });
    });

    // Subtotal
    if (subtotalEl) subtotalEl.textContent = formatMoney(total);

    // Afterpay
    if (afterpayEl && afterpayAmt) {
      afterpayAmt.textContent = formatMoney(Math.round(total / 4));
      afterpayEl.style.display = total > 0 ? 'block' : 'none';
    }

    // Free shipping bar
    if (total >= FREE_SHIPPING_THRESHOLD) {
      if (shipBarEl)  shipBarEl.style.display  = 'none';
      if (freeShipEl) freeShipEl.style.display = 'block';
    } else {
      if (freeShipEl) freeShipEl.style.display = 'none';
      if (shipBarEl)  shipBarEl.style.display  = 'block';
      var remaining = FREE_SHIPPING_THRESHOLD - total;
      var pct = Math.round((total / FREE_SHIPPING_THRESHOLD) * 100);
      if (shipRemEl)  shipRemEl.textContent = formatMoney(remaining);
      if (shipFillEl) shipFillEl.style.width = Math.min(pct, 100) + '%';
    }
  }

  /* ── REFRESH CART FROM API ── */
  function refreshCart() {
    fetchCart().then(renderCart).catch(function (e) {
      console.error('Sai cart fetch error:', e);
    });
  }

  /* ── UPDATE ITEM QUANTITY ── */
  function updateItem(key, quantity) {
    // Optimistic disable
    var item = document.querySelector('[data-key="' + key + '"]');
    if (item) item.style.opacity = '0.5';

    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity })
    })
    .then(function (r) { return r.json(); })
    .then(function (cart) {
      renderCart(cart);
      document.dispatchEvent(new CustomEvent('sai:cart:updated', { detail: cart }));
    })
    .catch(function (e) {
      console.error('Sai cart update error:', e);
      refreshCart();
    });
  }

  /* ── ADD TO CART (global handler) ── */
  function addToCart(variantId, quantity, callback) {
    quantity = quantity || 1;
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: parseInt(variantId, 10), quantity: quantity })
    })
    .then(function (r) { return r.json(); })
    .then(function (item) {
      if (item.id) {
        lastAddedContext = item;
        openDrawer();
        loadRecs(item);
        document.dispatchEvent(new CustomEvent('sai:cart:updated'));
        if (callback) callback(null, item);
      } else {
        if (callback) callback(item);
      }
    })
    .catch(function (e) {
      console.error('Sai add to cart error:', e);
      if (callback) callback(e);
    });
  }

  /* ── INTERCEPT THEME ADD-TO-CART FORMS ── */
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || form.getAttribute('action') !== '/cart/add') return;
    var idInput = form.querySelector('[name="id"]');
    if (!idInput) return;
    e.preventDefault();

    var variantId = idInput.value;
    var qty = form.querySelector('[name="quantity"]');
    var quantity = qty ? parseInt(qty.value, 10) : 1;
    var btn = form.querySelector('[type="submit"]');
    var origText = btn ? btn.textContent : '';

    if (btn) { btn.textContent = 'Adding…'; btn.disabled = true; }

    addToCart(variantId, quantity, function (err) {
      if (btn) { btn.textContent = err ? 'Try again' : 'Added ✓'; btn.disabled = false; }
      if (!err && btn) {
        setTimeout(function () { btn.textContent = origText; }, 2000);
      }
    });
  }, true);

  /* ── INTERCEPT AJAX ATC BUTTONS (.sai-pc-atc) ── */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('.sai-pc-atc');
    if (!btn) return;
    var vid = btn.dataset.variantId;
    if (!vid) return;
    e.preventDefault();
    var orig = btn.textContent;
    btn.textContent = 'Adding…';
    btn.disabled = true;
    addToCart(vid, 1, function (err) {
      btn.textContent = err ? 'Try again' : 'Added ✓';
      btn.disabled = false;
      setTimeout(function () { btn.textContent = orig; btn.disabled = false; }, 2000);
    });
  });

  /* ── OPEN DRAWER ON CART ICON CLICK ── */
  document.addEventListener('click', function (e) {
    var cartTrigger = e.target.closest('[href="/cart"], [data-cart-toggle], .cart-icon, .header-cart, .icon-cart, .sai-cart-trigger');
    if (!cartTrigger) return;
    e.preventDefault();
    openDrawer();
  });

  /* ── REC HELPERS (Option 2 — handle/title-based print + category guess) ── */
  function buildRecSources(item) {
    var handle = (item && item.handle) ? item.handle : '';
    var title  = (item && (item.product_title || item.title) || '').toLowerCase();
    var parts  = handle.split('-').filter(Boolean);
    var sources = [];

    // Tier 1 — same print. Try -print variants first at each depth,
    // two-word base ahead of one-word base.
    if (parts.length >= 2) {
      var twoWord = parts[0] + '-' + parts[1];
      sources.push(twoWord + '-print', twoWord); // e.g. bubble-gum-print, bubble-gum
    }
    if (parts.length >= 1) {
      sources.push(parts[0] + '-print', parts[0]); // e.g. bubble-print, bubble
    }

    // Tier 2 — same category fallback.
    var hay = (handle + ' ' + title).toLowerCase();
    if (/kaftan|caftan|goddess/.test(hay))             { sources.push('goddess-kaftans-main', 'kaftans-main'); }
    else if (/pant|trouser/.test(hay))                 { sources.push('pants'); }
    else if (/skirt/.test(hay))                        { sources.push('skirts'); }
    else if (/shirtdress|shirt-dress|dress/.test(hay)) { sources.push('shirt-dresses', 'dresses-main'); }
    else if (/blouse|top/.test(hay))                   { sources.push('tops'); }

    // Tier 3 — New Arrivals, always last.
    sources.push(RECS_COLLECTION);

    var seen = {}, ordered = [];
    sources.forEach(function (h) { if (h && !seen[h]) { seen[h] = 1; ordered.push(h); } });
    return ordered;
  }

  function fetchFirstUsable(sources, cartProductIds, idx) {
    idx = idx || 0;
    if (idx >= sources.length) return Promise.resolve([]);
    return fetch('/collections/' + sources[idx] + '/products.json?limit=12')
      .then(function (r) { return r.ok ? r.json() : { products: [] }; })
      .then(function (data) {
        var products = (data && data.products) ? data.products : [];
        var usable = products.filter(function (p) { return cartProductIds.indexOf(p.id) === -1; });
        if (usable.length > 0) return usable;
        return fetchFirstUsable(sources, cartProductIds, idx + 1);
      })
      .catch(function () { return fetchFirstUsable(sources, cartProductIds, idx + 1); });
  }

  /* ── YOU MAY ALSO LOVE — same print → same category → New Arrivals last ── */
  function loadRecs(contextItem) {
    if (!recsGrid) return;
    recsGrid.innerHTML = '';

    function run(ctx, cartProductIds) {
      fetchFirstUsable(buildRecSources(ctx), cartProductIds).then(function (recs) {
        recs = recs.slice(0, 4);
        if (recs.length % 2 !== 0) recs = recs.slice(0, recs.length - 1);

        if (recs.length === 0) {
          recsGrid.innerHTML = '';
          if (recsEl) recsEl.style.display = 'none';
          return;
        }

        recsGrid.innerHTML = recs.map(function (p) {
          var variant = p.variants[0];
          var img = p.images[0] ? p.images[0].src.replace('.jpg', '_180x.jpg').replace('.png', '_180x.png') : '';
          var price = '$' + Number(variant.price).toFixed(2);
          return [
            '<div class="sai-cart-rec-card">',
              '<a href="/products/' + p.handle + '" class="sai-cart-rec-img">',
                img ? '<img src="' + img + '" alt="' + p.title.replace(/"/g, '&quot;') + '" loading="lazy">' : '',
              '</a>',
              '<div class="sai-cart-rec-info">',
                '<div class="sai-cart-rec-name">' + p.title + '</div>',
                '<div class="sai-cart-rec-price">' + price + '</div>',
              '</div>',
              variant.available
                ? '<button class="sai-cart-rec-atc" data-variant-id="' + variant.id + '">Add</button>'
                : '<button class="sai-cart-rec-atc" disabled>Sold out</button>',
            '</div>'
          ].join('');
        }).join('');

        if (recsEl) recsEl.style.display = 'block';

        recsGrid.querySelectorAll('.sai-cart-rec-atc').forEach(function (btn) {
          btn.addEventListener('click', function () {
            var vid = this.dataset.variantId;
            if (!vid) return;
            var self = this;
            self.textContent = '…';
            self.disabled = true;
            addToCart(vid, 1, function (err) {
              self.textContent = err ? 'Try again' : '✓';
              setTimeout(function () { self.textContent = 'Add'; self.disabled = false; }, 1500);
            });
          });
        });
      });
    }

    fetchCart().then(function (cart) {
      if (!cart.items || cart.items.length === 0) {
        if (recsEl) recsEl.style.display = 'none';
        return;
      }
      var cartProductIds = cart.items.map(function (i) { return i.product_id; });

      var ctx = null;
      if (contextItem && (contextItem.handle || contextItem.product_title)) {
        ctx = contextItem;
      }
      if (!ctx && lastAddedContext) {
        var stillPresent = cart.items.some(function (i) {
          return (lastAddedContext.key && i.key === lastAddedContext.key) ||
                 (lastAddedContext.product_id && i.product_id === lastAddedContext.product_id) ||
                 (lastAddedContext.id && i.product_id === lastAddedContext.id);
        });
        if (stillPresent) ctx = lastAddedContext;
      }
      if (!ctx) ctx = cart.items[0];

      run(ctx, cartProductIds);
    }).catch(function () {
      if (recsEl) recsEl.style.display = 'none';
    });
  }

  /* ── LISTEN FOR CUSTOM OPEN EVENT ── */
  document.addEventListener('sai:cart:open', function () {
    openDrawer();
  });

  /* ── LISTEN FOR THEME CART EVENTS ── */
  document.addEventListener('cart:updated', refreshCart);
  document.addEventListener('sai:cart:updated', refreshCart);

  /* ── INIT ── */
  loadRecs();
  fetchCart().then(function (cart) {
    if (countEl) {
      countEl.textContent = cart.item_count;
      countEl.style.display = cart.item_count > 0 ? 'flex' : 'none';
    }
    document.querySelectorAll('[data-cart-count], .cart-count, .header-cart-count').forEach(function (el) {
      el.textContent = cart.item_count;
    });
  });

  window.SaiCart = {
    open: openDrawer,
    close: closeDrawer,
    add: addToCart,
    refresh: refreshCart
  };

})();